import { Box, mdBumps, TertiaryButton, Typo } from '@wrisk/ui-components'
import type { TFunction } from 'i18next'
import { isNil, negate } from 'lodash-es'
import { FunctionComponent, useCallback } from 'react'
import React from 'react'
import { Controller } from 'react-hook-form'

import { TKeyBuilder } from '../../../../infrastructure/internationalisation'
import { InputConfig, InputLoopConfig } from '../../../../state/configuration'
import { FormFormatter } from '../../FormFormatter'

export interface InputLoopSummaryProps {
  config: InputLoopConfig
  index: number
  t: TFunction
  tKey: TKeyBuilder
  onRemove?: (index: number) => void
}

function hasSummary(config: InputLoopConfig) {
  return (input: InputConfig) =>
    isNil(config.meta?.summary) || config.meta.summary.includes(input.name)
}

export const InputLoopSummary: FunctionComponent<InputLoopSummaryProps> = ({
  t,
  tKey,
  config,
  index,
  onRemove,
}: InputLoopSummaryProps & {
  onRemove?: (index: number) => void
}) => {
  const handleRemove = useCallback(() => onRemove?.(index), [index, onRemove])

  return (
    <Controller
      name={`${config.name}.${index}`}
      render={({ field: { value } }) => (
        <Box
          borderWidth={1}
          alignItems='center'
          display='flex'
          justifyContent='space-between'
          gap={mdBumps}
          p={mdBumps}
          width={1}
        >
          <Box as='ul' display='flex' gap={[2, 2, 0]} flexDirection='column'>
            {config.meta?.form
              .filter(negate(isNil))
              .filter(hasSummary(config))
              .map((input) => {
                const inputValue = value?.[input.name]

                if (isNil(inputValue)) return null

                return (
                  <Box
                    display='flex'
                    flexDirection={['column', 'column', 'row']}
                    columnGap={1}
                    key={`${index}-${input.name}`}
                    as='li'
                  >
                    <Typo fontFamily='input' fontWeight='bold'>
                      {t(tKey(config.name, 'inputs', input.name, 'summary'))}:
                    </Typo>
                    <Typo fontFamily='input'>
                      <FormFormatter input={input} value={inputValue} t={t} />
                    </Typo>
                  </Box>
                )
              })}
          </Box>

          <TertiaryButton layout='small' onClick={handleRemove}>
            Remove
          </TertiaryButton>
        </Box>
      )}
    />
  )
}
